// Pure utility functions for job operations
import { formatInTimeZone } from 'date-fns-tz';

// Memoization helper for performance optimization
const memoize = (fn) => {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

// Helper function to format job category text
const _formatJobCategory = (category) => {
  if (category === 'CLEANING') return 'Clean';
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
};
export const formatJobCategory = memoize(_formatJobCategory);

// Helper function to convert 24h time to 12h format with AM/PM
const _formatTime = (time) => {
  if (!time) return '';
  
  // Format the time in 12-hour format
  try {
    if (time.includes(':')) { // It's already a time string
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${minutes} ${ampm}`;
    } else {
      return time;
    }
  } catch (e) {
    return time;
  }
};
export const formatTime = memoize(_formatTime);

// Helper function to check if a job is an owner arrival
export const isOwnerArrival = (job) => {
  return job.isOwnerArrival === true;
};

// Helper function to get the appropriate time for a job based on its service type
export const getCheckoutTime = (job) => {
  // For manually created jobs (no reservation DATA), use startTime
  if (!job.reservationId && !job.reservation && job.startTime) {
    return job.startTime;
  }
  
  // If it's a manually created job without startTime, use a default time
  if (!job.reservationId && !job.reservation && !job.startTime) {
    return '10:00'; // Default to 10:00 AM for manually created jobs without time
  }
  
  // For CHECKIN-based jobs (like inspections), use the job's startTime if available
  // The backend properly sets startTime to the previous reservation's checkout time
  if (job.serviceType?.timeBase === 'CHECKIN') {
    // Use job's startTime if available (this is set by the backend to previous reservation's checkout time)
    if (job.startTime) {
      return job.startTime;
    }
    
    // Use previous reservation's checkout time for CHECKIN-based jobs
    if (job.previousReservation) {
      // Check for time override first
      if (job.previousReservation.has_time_override && job.previousReservation.override_check_out) {
        // Extract time from ISO string
        const timeMatch = job.previousReservation.override_check_out.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (timeMatch) {
          const [, hours, minutes] = timeMatch;
          return `${hours}:${minutes}`;
        }
        return job.previousReservation.override_check_out;
      }
      
      // Use regular checkout time if available
      if (job.previousReservation.check_out_time) {
        return job.previousReservation.check_out_time;
      }
      
      // Extract time from check_out date if no explicit time is set
      if (job.previousReservation.check_out) {
        try {
          const checkoutDate = new Date(job.previousReservation.check_out);
          const hours = checkoutDate.getHours().toString().padStart(2, '0');
          const minutes = checkoutDate.getMinutes().toString().padStart(2, '0');
          return `${hours}:${minutes}`;
        } catch (error) {
          // Error parsing date - fallback to default behavior
        }
      }
    }
    
    // Use override check-in time if available (fallback)
    if (job.reservation?.has_time_override && job.reservation?.override_check_in) {
      // Extract time from ISO string
      const timeMatch = job.reservation.override_check_in.match(/T(\d{2}):(\d{2}):(\d{2})/);
      if (timeMatch) {
        const [, hours, minutes] = timeMatch;
        const result = `${hours}:${minutes}`;
        return result;
      }
      return job.reservation.override_check_in;
    }
    
    // If job has a reservation with check_in_time, use that (fallback)
    if (job.reservation?.check_in_time) {
      return job.reservation.check_in_time;
    }
    
    // Fallback to property check-in time
    const fallback = job.property?.checkInTime || '15:00';
    return fallback; // Default check-in time
  }
  
  // For CHECKOUT-based jobs (like cleaning), use checkout times
  // Use override checkout time if available
  if (job.reservation?.has_time_override && job.reservation?.override_check_out) {
    // Extract time from ISO string if it's in ISO format
    const timeMatch = job.reservation.override_check_out.match(/T(\d{2}):(\d{2}):(\d{2})/);
    if (timeMatch) {
      const [, hours, minutes] = timeMatch;
      const result = `${hours}:${minutes}`;
      return result;
    }
    return job.reservation.override_check_out;
  }
  
  // If job has a reservation with check_out_time, use that
  if (job.reservation?.check_out_time) {
    return job.reservation.check_out_time;
  }
  
  // Fallback to property checkout time
  const fallback = job.property?.checkOutTime || '10:00';
  return fallback; // Default checkout time
};

// Helper function to determine if a checkout is late
// NOTE: For CHECKIN jobs, the JobBadges component now uses backend detection via useLateCheckoutForCustomJob hook
// This function is kept for backward compatibility and non-CHECKIN jobs
export const isLateCheckout = (job) => {
  // For CHECKIN-based service types, check if the previous reservation had a late checkout
  if (job.serviceType?.timeBase === 'CHECKIN') {
    // Handle custom jobs without reservations
    if (!job.reservationId && !job.reservation) {
      // For custom jobs, we need to check if there's a reservation checking in on the same date
      // and if that reservation's previous checkout was late
      // This will be handled by a GraphQL query for performance
      return job.hasLateCheckoutFlag || false;
    }

    // Original logic for reservation-linked jobs
    // Check if there's a previous reservation with checkout override
    if (!job.previousReservation) {
      return false;
    }
    
    const prevReservation = job.previousReservation;
    
    // Must have a checkout time override to be considered "late"
    if (!prevReservation.has_time_override || !prevReservation.override_check_out) {
      return false;
    }
    
    // NEW: Check if there's a gap between previous checkout and current check-in
    // Only show late checkout flag if reservations are on the same day or consecutive days
    if (job.reservation) {
      try {
        // Get the date of the previous reservation's checkout
        const prevCheckoutDate = new Date(prevReservation.override_check_out || prevReservation.check_out);
        
        // Get the date of the current reservation's check-in
        const currentCheckinDate = new Date(job.reservation.override_check_in || job.reservation.check_in);
        
        // Calculate the difference in days using date-only comparison
        // This ensures we're comparing calendar days, not just 24-hour periods
        const prevCheckoutDateOnly = new Date(prevCheckoutDate.getFullYear(), prevCheckoutDate.getMonth(), prevCheckoutDate.getDate());
        const currentCheckinDateOnly = new Date(currentCheckinDate.getFullYear(), currentCheckinDate.getMonth(), currentCheckinDate.getDate());
        const daysDiff = Math.round((currentCheckinDateOnly - prevCheckoutDateOnly) / (1000 * 60 * 60 * 24));
        
        // Only show late checkout flag for same-day turnovers
        // If there's any gap (even 1 day), hide the flag as it's not relevant for inspection timing
        if (daysDiff > 0) {
          return false;
        }
      } catch (e) {
        // If there's an error parsing dates, default to showing the flag
        // This maintains backward compatibility
      }
    }
    
    // Get the standard checkout time for comparison
    const standardCheckoutTime = prevReservation.check_out_time || 
                                job.property?.checkOutTime || 
                                job.property?.check_out_time ||
                                '10:00'; // Default checkout time
    
    if (!standardCheckoutTime) {
      return false;
    }
    
    // Parse standard checkout time
    let standardHours, standardMinutes;
    try {
      if (standardCheckoutTime.includes(':') && !standardCheckoutTime.includes('T')) {
        [standardHours, standardMinutes] = standardCheckoutTime.split(':').map(Number);
      } else {
        const standardDate = new Date(standardCheckoutTime);
        standardHours = standardDate.getHours();
        standardMinutes = standardDate.getMinutes();
      }
    } catch (e) {
      return false;
    }
    
    // Parse override checkout time from ISO string
    let overrideHours, overrideMinutes;
    try {
      const timeMatch = prevReservation.override_check_out.match(/T(\d{2}):(\d{2}):(\d{2})/);
      if (timeMatch) {
        const [, hours, minutes] = timeMatch;
        overrideHours = parseInt(hours, 10);
        overrideMinutes = parseInt(minutes, 10);
      } else {
        // Fallback to Date parsing if regex fails
        const overrideDate = new Date(prevReservation.override_check_out);
        overrideHours = overrideDate.getHours();
        overrideMinutes = overrideDate.getMinutes();
      }
    } catch (e) {
      return false;
    }
    
    // Compare: override checkout time is later than standard checkout time
    const isLate = (overrideHours > standardHours) || 
                  (overrideHours === standardHours && overrideMinutes > standardMinutes);
    
    return isLate;
  }

  // For CHECKOUT-based service types, use the original logic
  // Check if this job has any time overrides (either job-level or reservation-level)
  const hasJobTimeOverride = job.hasTimeOverride;
  const hasReservationTimeOverride = job.reservation?.has_time_override && job.reservation?.override_check_out;
  
  if (!hasJobTimeOverride && !hasReservationTimeOverride) {
    return false;
  }
  
  // Get the current/actual time (either job's start time or reservation override)
  let actualTime;
  
  if (hasJobTimeOverride && job.startTime) {
    // Job has its own time override - use the job's start time
    actualTime = job.startTime;
  } else if (hasReservationTimeOverride) {
    // Use reservation's override checkout time
    actualTime = job.reservation.override_check_out;
  } else {
    return false;
  }
  
  // Get the original/standard time for comparison
  const standardTime = job.reservation?.check_out_time || 
                      job.property?.checkOutTime || 
                      job.property?.check_out_time ||
                      '10:00'; // Default checkout time
  
  if (!standardTime || !actualTime) {
    return false;
  }
  
  // Parse standard time
  let standardHours, standardMinutes;
  try {
    if (standardTime.includes(':') && !standardTime.includes('T')) {
      [standardHours, standardMinutes] = standardTime.split(':').map(Number);
    } else {
      const standardDate = new Date(standardTime);
      standardHours = standardDate.getHours();
      standardMinutes = standardDate.getMinutes();
    }
  } catch (e) {
    return false;
  }
  
  // Parse actual time  
  let actualHours, actualMinutes;
  try {
    if (actualTime.includes(':') && !actualTime.includes('T')) {
      // Time string format like "14:00"
      [actualHours, actualMinutes] = actualTime.split(':').map(Number);
    } else {
      // Date string format like "2025-06-07T12:00:00.000Z" - use timezone-safe parsing
      const timeMatch = actualTime.match(/T(\d{2}):(\d{2}):(\d{2})/);
      if (timeMatch) {
        const [, hours, minutes] = timeMatch;
        actualHours = parseInt(hours, 10);
        actualMinutes = parseInt(minutes, 10);
      } else {
        // Fallback to Date parsing if regex fails
        const actualDate = new Date(actualTime);
        actualHours = actualDate.getHours();
        actualMinutes = actualDate.getMinutes();
      }
    }
  } catch (e) {
    return false;
  }
  
  // Compare: actual time is later than standard time
  const isLate = (actualHours > standardHours) || 
                (actualHours === standardHours && actualMinutes > standardMinutes);
  
  return isLate;
};

// Helper function to determine if a check-in is early
// NOTE: This logic has been moved to backend GraphQL resolver for better performance
// Now we simply check the computed field from the backend
export const isEarlyCheckin = (job) => {
  return job.isEarlyCheckin === true;
};

// Function to determine the left border color based on job type and flip status
export const getBorderColor = (job) => {
  if (job.category === 'CLEANING') {
    if (job.cleaningType === 'FLIP') {
      return 'border-l-4 border-red-500 pl-2'; // Red border for Flip cleans
    }
    return 'border-l-4 border-blue-500 pl-2'; // Blue border for normal cleans
  } else if (job.category === 'WALKTHROUGH') {
    return 'border-l-4 border-blue-300 pl-2'; // Light blue for walkthrough
  } else if (job.category === 'INSPECTION') {
    return 'border-l-4 border-green-500 pl-2'; // Green for inspection
  }
  return 'pl-2'; // No border for other job types, but keep padding consistent
};

// Function to get enhanced styling for flip jobs
export const getFlipJobStyling = (job) => {
  const isFlipJob = job.isFlip;
  return {
    isFlipJob,
    bgColor: 'bg-white', // Keep standard background color
    borderClass: isFlipJob ? 'border border-red-200' : 'border border-gray-200',
    leftBorderClass: isFlipJob ? 'border-l-[24px] border-red-500' : ''
  };
};

// Helper function to determine if a job is a manually created job (no reservation)
export const isManualJob = (job) => {
  return !job.reservationId && job.startTime;
};

// Helper function to format dates for jobs
export const formatJobDate = (date) => {
  if (!date) return 'No date';
  try {
    // Use formatInTimeZone to ensure consistent timezone handling
    return formatInTimeZone(new Date(date), 'America/New_York', 'MMM d, yyyy');
  } catch (e) {
    return date;
  }
}; 